import React, {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';

import Modal from '../../../../components/Modal/Modal';
import Alert from '../../../../components/Alert/Alert';
import AlertType from '../../../../common/alertType';
import Text from '../../../../components/Text/Text';
import {
  IconArrowDown, IconCart, IconLoading, IconReload
} from '../../../../assets/icon';
import {
  getSupplierAvailability,
  getSupplierOrderSettings
} from '../../../../services/supplierOrders';

const allSuppliers = [
  'Turn14', 'Premier', 'RockAuto', 'Comps', 'Keystone', 'Meyer', 'Etristate', 'RCD'
];
const filters = [
  { value: 'all', label: 'All' },
  { value: 'available', label: 'Available' },
  { value: 'issues', label: 'Issues' },
];

const hideBrokenImage = ({ currentTarget }) => {
  const image = currentTarget;
  image.hidden = true;
};

const getErrorMessage = (error, supplier) => error?.data?.supplier_errors?.[supplier]
  || error?.data?.message
  || error?.message
  || 'Could not load supplier availability.';

const canPurchase = (match) => Boolean(match?.can_purchase) && (
  match.order_method === 'email'
  || (match.total_available > 0 && match.purchase_cost !== null)
);

const defaultWarehouseCode = (match) => {
  if (['Keystone', 'Meyer'].includes(match.supplier)) return 'AUTO';
  if (match.order_method === 'email') return 'EMAIL';
  return match.warehouses?.[0]?.code;
};

const SupplierAvailabilityModal = ({
 visible, item, onClose, onAdd
}) => {
  const requestVersion = useRef(0);
  const [matches, setMatches] = useState([]);
  const [supplierErrors, setSupplierErrors] = useState({});
  const [supplierMessages, setSupplierMessages] = useState({});
  const [supplierStatuses, setSupplierStatuses] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const [expandedSupplier, setExpandedSupplier] = useState();
  const [suppliers, setSuppliers] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsError, setSettingsError] = useState();

  const requestSupplier = useCallback((supplier, version = requestVersion.current) => {
    setSupplierStatuses((current) => ({ ...current, [supplier]: 'loading' }));
    setSupplierErrors((current) => ({ ...current, [supplier]: undefined }));
    setSupplierMessages((current) => ({ ...current, [supplier]: undefined }));

    return getSupplierAvailability({
      mpn: item?.mpn,
      brand: item?.brand,
      supplier,
    })
      .then(({ data, message }) => {
        if (requestVersion.current !== version) return;

        setSupplierMessages((current) => ({ ...current, [supplier]: message }));
        setMatches((current) => [
          ...current.filter((match) => match.supplier !== supplier),
          ...(data || []),
        ]);
        setSupplierStatuses((current) => ({ ...current, [supplier]: 'complete' }));
      })
      .catch((requestError) => {
        if (requestVersion.current !== version) return;

        setSupplierErrors((current) => ({
          ...current,
          [supplier]: getErrorMessage(requestError, supplier),
        }));
        setSupplierStatuses((current) => ({ ...current, [supplier]: 'error' }));
      });
  }, [item?.brand, item?.mpn]);

  useEffect(() => {
    if (!visible || !item?.mpn) return undefined;

    const version = requestVersion.current + 1;
    requestVersion.current = version;
    setMatches([]);
    setSupplierErrors({});
    setSupplierMessages({});
    setSupplierStatuses({});
    setActiveFilter('all');
    setExpandedSupplier(undefined);
    setSuppliers([]);
    setSettingsError(undefined);
    setSettingsLoading(true);

    getSupplierOrderSettings()
      .then((settings) => {
        if (requestVersion.current !== version) return;
        const disabled = new Set(settings.disabled_suppliers || []);
        const enabled = (settings.integrations || [])
          .map(({ supplier }) => supplier)
          .filter((supplier) => allSuppliers.includes(supplier) && !disabled.has(supplier));
        const enabledSuppliers = enabled.length || settings.integrations?.length
          ? enabled
          : allSuppliers.filter((supplier) => !disabled.has(supplier));
        setSuppliers(enabledSuppliers);
        enabledSuppliers.forEach((supplier) => requestSupplier(supplier, version));
      })
      .catch((requestError) => {
        if (requestVersion.current === version) {
          setSettingsError(getErrorMessage(requestError));
        }
      })
      .finally(() => {
        if (requestVersion.current === version) setSettingsLoading(false);
      });

    return () => {
      if (requestVersion.current === version) requestVersion.current += 1;
    };
  }, [visible, item?.mpn, requestSupplier]);

  const matchBySupplier = useMemo(
    () => new Map(matches.map((match) => [match.supplier, match])),
    [matches]
  );
  const availableMatches = useMemo(() => matches.filter(canPurchase), [matches]);
  const bestSupplier = useMemo(() => availableMatches
    .filter(({ purchase_cost: purchaseCost }) => purchaseCost !== null)
    .reduce((best, match) => (
      !best || match.purchase_cost < best.purchase_cost ? match : best
    ), undefined)?.supplier, [availableMatches]);
  const completedCount = suppliers.filter(
    (supplier) => ['complete', 'error'].includes(supplierStatuses[supplier])
  ).length;
  const issueCount = suppliers.filter((supplier) => {
    const status = supplierStatuses[supplier];
    const match = matchBySupplier.get(supplier);
    return status === 'error' || (status === 'complete' && !canPurchase(match));
  }).length;
  const visibleSuppliers = suppliers.filter((supplier) => {
    const status = supplierStatuses[supplier];
    const match = matchBySupplier.get(supplier);
    if (activeFilter === 'available') return canPurchase(match);
    if (activeFilter === 'issues') {
      return status === 'error' || (status === 'complete' && !canPurchase(match));
    }
    return true;
  });

  const addMatch = (match) => onAdd({
    ...match,
    type: item.type,
    quantity: 1,
    warehouse_code: defaultWarehouseCode(match),
  });

  return (
    <Modal
      visible={visible}
      toggleVisible={onClose}
      title="Supplier availability"
      subTitle={item?.mpn ? `${item.brand || ''} ${item.mpn}` : undefined}
      showFooter={false}
    >
      <div className="w-[900px] max-w-full max-h-[70vh] overflow-auto customScroll px-[2px] sm:px-[4px]">
        <div
          className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-[10px]
            py-[12px] border-y border-whiteHover dark:border-[#1b263b]`}
        >
          <Text text={`${completedCount} of ${suppliers.length} suppliers checked`} muted />
          <div className="flex flex-wrap gap-[6px]">
            {filters.map(({ value, label }) => {
              let count = issueCount;
              if (value === 'all') count = suppliers.length;
              if (value === 'available') count = availableMatches.length;
              return (
                <button
                  key={value}
                  type="button"
                  className={`px-[10px] py-[7px] rounded-[6px] text-[12px] font-medium transition-colors
                    ${activeFilter === value
                    ? 'bg-primary text-white'
                    : `bg-background-main text-text-muted hover:text-primary
                      dark:bg-[#1b263b] dark:text-secondary`}`}
                  onClick={() => setActiveFilter(value)}
                >
                  {`${label} ${count}`}
                </button>
              );
            })}
          </div>
        </div>

        {settingsError && (
          <div className="mt-[12px]">
            <Alert text={settingsError} type={AlertType.warning} />
          </div>
        )}

        {settingsLoading && (
          <div className="py-[28px] flex items-center justify-center gap-[8px] text-text-muted">
            <IconLoading className="w-[20px] h-[20px] animate-spin" />
            <span>Loading enabled suppliers...</span>
          </div>
        )}

        <div
          className={`hidden lg:grid lg:grid-cols-[minmax(145px,1.2fr)_minmax(150px,1fr)_100px_minmax(130px,1fr)_100px]
            gap-[12px] px-[12px] py-[11px] text-[11px] font-medium uppercase tracking-[0.04em]
            text-text-muted`}
        >
          <span>Supplier</span>
          <span>Status</span>
          <span>Price</span>
          <span>Fulfillment</span>
          <span />
        </div>

        <div className="flex flex-col">
          {visibleSuppliers.map((supplier) => {
            const status = supplierStatuses[supplier] || 'loading';
            const match = matchBySupplier.get(supplier);
            const available = canPurchase(match);
            const isEmailOrder = match?.order_method === 'email';
            const isBestPrice = supplier === bestSupplier;
            const expanded = expandedSupplier === supplier;
            const warehouses = match?.warehouses || [];
            const firstWarehouse = warehouses[0];
            let fulfillment = status === 'loading' ? 'Waiting for supplier' : 'Unavailable';
            if (isEmailOrder) fulfillment = 'Order request by email';
            if (firstWarehouse && !isEmailOrder) {
              fulfillment = firstWarehouse.name;
              if (warehouses.length > 1) fulfillment += ` +${warehouses.length - 1}`;
            }
            const emptyMessage = supplierMessages[supplier]
              || `${supplier}: no matching available items found.`;
            let availabilityText = 'Not available';
            if (available) availabilityText = `In stock · ${match.total_available}`;
            if (isEmailOrder) availabilityText = 'Order by email';
            let priceText = '—';
            let priceIsKnown = false;
            if (isEmailOrder) priceText = 'Quoted later';
            if (match?.purchase_cost !== null && match?.purchase_cost !== undefined) {
              priceText = `$${match.purchase_cost.toFixed(2)}`;
              priceIsKnown = true;
            }
            let warehouseDetails = <Text text={`Out of stock at all ${supplier} warehouses`} muted />;
            if (warehouses.length) {
              warehouseDetails = warehouses.map((warehouse) => (
                <span
                  key={warehouse.code}
                  className={`px-[9px] py-[5px] rounded-[5px] bg-white dark:bg-[#0d1b2a]
                    text-[12px] text-text-main dark:text-secondary`}
                >
                  {`${warehouse.name}: ${warehouse.quantity}`}
                </span>
              ));
            }
            if (isEmailOrder) {
              warehouseDetails = <Text text="No availability check is required" muted />;
            }

            return (
              <div
                key={supplier}
                className={`${isBestPrice ? 'bg-primary/[0.04]' : ''}
                  border-b border-whiteHover dark:border-[#1b263b]`}
              >
                <div
                  className={`grid grid-cols-[minmax(0,1fr)_auto]
                    lg:grid-cols-[minmax(145px,1.2fr)_minmax(150px,1fr)_100px_minmax(130px,1fr)_100px]
                    items-center gap-x-[12px] gap-y-[6px] px-[8px] sm:px-[12px] py-[12px] text-[13px]`}
                >
                  <div className="col-start-1 row-start-1 flex items-center gap-[9px] min-w-0">
                    <span
                      className={`relative w-[40px] h-[40px] shrink-0 rounded-[7px] overflow-hidden
                        flex items-center justify-center bg-background-main dark:bg-[#1b263b]
                        text-primary text-[11px] font-semibold`}
                    >
                      {supplier === 'RockAuto' ? 'RA' : supplier.slice(0, 2).toUpperCase()}
                      {match?.thumbnail && (
                        <img
                          src={match.thumbnail}
                          alt={`${match.brand || supplier} ${match.mpn || item.mpn}`}
                          loading="lazy"
                          decoding="async"
                          className="absolute inset-0 w-full h-full object-contain bg-white"
                          onError={hideBrokenImage}
                        />
                      )}
                    </span>
                    <div className="min-w-0">
                      <Text text={supplier} bold />
                      {isBestPrice && <Text text="Best available price" textStyle="text-[11px] text-primary" />}
                    </div>
                  </div>

                  <div className="col-start-1 row-start-2 lg:col-start-auto lg:row-start-auto">
                    {status === 'loading' && (
                      <div className="flex items-center gap-[7px] text-warning">
                        <IconLoading className="w-[17px] h-[17px] spin shrink-0" />
                        <span>Checking…</span>
                      </div>
                    )}
                    {status === 'error' && (
                      <div className="flex items-center gap-[7px] text-danger">
                        <span className="w-[7px] h-[7px] rounded-full bg-danger shrink-0" />
                        <span>Request failed</span>
                      </div>
                    )}
                    {status === 'complete' && (
                      <div
                        className={`flex items-center gap-[7px]
                          ${available ? 'text-success' : 'text-text-muted'}`}
                      >
                        <span
                          className={`w-[7px] h-[7px] rounded-full shrink-0
                            ${available ? 'bg-success' : 'bg-text-muted'}`}
                        />
                        <span>{availabilityText}</span>
                      </div>
                    )}
                  </div>

                  <div className="col-start-2 row-start-1 lg:col-start-auto lg:row-start-auto text-right lg:text-left">
                    <Text text={priceText} bold={priceIsKnown} muted={!priceIsKnown} />
                  </div>

                  <div
                    className="col-start-1 row-start-3 lg:col-start-auto lg:row-start-auto text-text-muted truncate"
                    title={fulfillment}
                  >
                    {fulfillment}
                  </div>

                  <div
                    className={`col-start-2 row-start-2 row-span-2 lg:col-start-auto lg:row-start-auto lg:row-span-1
                      flex justify-end items-center gap-[6px]`}
                  >
                    {status === 'error' && (
                      <button
                        type="button"
                        className={`h-[38px] px-[10px] rounded-[6px] flex items-center gap-[6px]
                          bg-background-main dark:bg-[#1b263b] text-text-main dark:text-secondary text-[12px]`}
                        onClick={() => requestSupplier(supplier)}
                      >
                        <IconReload className="w-[16px] h-[16px]" />
                        Retry
                      </button>
                    )}
                    {match && (
                      <button
                        type="button"
                        aria-label={`${expanded ? 'Hide' : 'Show'} ${supplier} details`}
                        aria-expanded={expanded}
                        className={`w-[38px] h-[38px] rounded-[6px] flex items-center justify-center
                          bg-background-main dark:bg-[#1b263b] text-text-muted`}
                        onClick={() => setExpandedSupplier(expanded ? undefined : supplier)}
                      >
                        <IconArrowDown
                          className={`w-[16px] h-[16px] transition-transform ${expanded ? 'rotate-180' : ''}`}
                        />
                      </button>
                    )}
                    {available && (
                      <button
                        type="button"
                        aria-label={`Add ${supplier} to supplier cart`}
                        className={`w-[38px] h-[38px] rounded-[6px] flex items-center justify-center
                          bg-primary text-white`}
                        onClick={() => addMatch(match)}
                      >
                        <IconCart className="w-[17px] h-[17px]" />
                      </button>
                    )}
                  </div>
                </div>

                {status === 'error' && (
                  <div className="px-[8px] sm:px-[55px] pb-[12px] text-[12px] text-danger break-words">
                    {supplierErrors[supplier]}
                  </div>
                )}
                {status === 'complete' && !match && (
                  <div className="px-[8px] sm:px-[55px] pb-[12px] text-[12px] text-text-muted break-words">
                    {emptyMessage}
                  </div>
                )}
                {expanded && match && (
                  <div className="px-[8px] sm:px-[55px] pb-[14px]">
                    <div className="p-[12px] rounded-[8px] bg-background-main dark:bg-[#1b263b]">
                      <div>
                        <Text text={match.product_name || `${supplier} item ${match.external_item_id}`} bold />
                        <Text text={`Item number: ${match.external_item_id}`} muted />
                        <div className="flex flex-wrap gap-[7px] mt-[9px]">
                          {warehouseDetails}
                        </div>
                        {match.notice && (
                          <div className="mt-[9px]">
                            <Alert text={match.notice} type={AlertType.important} />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {!settingsLoading && !settingsError && !visibleSuppliers.length && (
          <div className="py-[24px] text-center">
            <Text text="No suppliers match this filter." muted />
          </div>
        )}
      </div>
    </Modal>
  );
};

SupplierAvailabilityModal.propTypes = {
  visible: PropTypes.bool,
  item: PropTypes.shape({ mpn: PropTypes.string, brand: PropTypes.string, type: PropTypes.string }),
  onClose: PropTypes.func,
  onAdd: PropTypes.func,
};

SupplierAvailabilityModal.defaultProps = {
  visible: false,
  item: undefined,
  onClose: () => {},
  onAdd: () => {},
};

export default SupplierAvailabilityModal;
